/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { parseBoolean } from '@amazon/vinyl-util'
import { queryParam } from '@/util/queryParams'

/**
 * Shared reader for the `?feedStub=1` URL flag that swaps the live
 * Firefly feed / agent transport for the deterministic dev fixture
 * served by `feedStubPlugin`. Explicit opt-in only: a missing param,
 * `=0`, or the Vite dev-mode `__DEV__` define all route through the
 * real transport — init failures surface loudly via the standard
 * errorHandler path rather than silently falling back to the stub.
 */
export function isFeedStubEnabled(): boolean {
    try {
        const raw = queryParam('feedStub')
        if (raw != null) return parseBoolean(raw)
    } catch {
        // No URL context (non-browser transform path) — stub off.
    }
    return false
}

/**
 * Reader for the dev-only `?noTakeover=1` flag that makes the feed skip
 * the takeover interstitial so headless snaps capture the feed itself
 * rather than the full-screen scrim. Gated behind `?feedStub=1` — it is
 * inert without the stub, so it can never suppress a real takeover for a
 * signed-in user.
 */
export function isTakeoverBypassEnabled(): boolean {
    if (!isFeedStubEnabled()) return false
    try {
        const raw = queryParam('noTakeover')
        if (raw != null) return parseBoolean(raw)
    } catch {
        // No URL context — bypass off.
    }
    return false
}
